import React, { useState } from 'react';
import { Search, Package, Clock, CreditCard, CheckCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  deposit_received: { label: 'Deposit Received', color: 'bg-amber-100 text-amber-700', icon: CreditCard },
  partial_payment: { label: 'Partial Payment', color: 'bg-blue-100 text-blue-700', icon: CreditCard },
  paid_in_full: { label: 'Paid in Full', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  ready_to_ship: { label: 'Ready to Ship', color: 'bg-purple-100 text-purple-700', icon: Package },
  shipped: { label: 'Shipped', color: 'bg-indigo-100 text-indigo-700', icon: Package },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: AlertCircle }
};

const CheckPreOrderStatus = () => {
  const [orderCode, setOrderCode] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    
    if (!orderCode.trim() || !email.trim()) {
      toast.error('Please enter both your order code and email');
      return;
    }

    setLoading(true);
    setError('');
    setOrderData(null);

    try {
      const res = await axios.post(`${API_URL}/api/preorders/check-status`, {
        order_code: orderCode.trim().toUpperCase(),
        email: email.trim().toLowerCase()
      });
      
      setOrderData(res.data);
      setIsExpanded(true);
      toast.success('Pre-order found!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Pre-order not found. Please check your order code and email.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    return STATUS_LABELS[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: Package };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 overflow-hidden" data-testid="preorder-status-checker">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
        data-testid="preorder-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
            <Package className="h-5 w-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">Check Pre-Order Status</h3>
            <p className="text-sm text-gray-600">Track your order and see remaining balance</p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-amber-200">
          {/* Search Form */}
          {!orderData && (
            <form onSubmit={handleCheckStatus} className="pt-4 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order Code</label>
                <Input
                  value={orderCode}
                  onChange={(e) => setOrderCode(e.target.value.toUpperCase())}
                  placeholder="e.g., PO-ABC123"
                  className="font-mono uppercase"
                  data-testid="preorder-code-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="preorder-email-input"
                />
              </div>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="preorder-check-btn"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Check Status
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Order Details */}
          {orderData && (
            <div className="pt-4 space-y-4" data-testid="preorder-details">
              {/* Status Badge & Code */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Order:</span>
                  <span className="font-mono font-bold text-lg text-gray-900" data-testid="preorder-code-display">
                    {orderData.order_code}
                  </span>
                </div>
                <div className={`px-3 py-1 rounded text-sm font-medium ${getStatusInfo(orderData.status).color}`}>
                  {getStatusInfo(orderData.status).label}
                </div>
              </div>

              {/* Product Info */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <p className="font-medium text-gray-900" data-testid="preorder-product-name">{orderData.product_name}</p>
                <p className="text-sm text-gray-500">Ordered: {formatDate(orderData.created_at)}</p>
                {orderData.estimated_ship_date && (
                  <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                    <Clock className="h-4 w-4" />
                    Est. Ship Date: {formatDate(orderData.estimated_ship_date)}
                  </p>
                )}
              </div>

              {/* Payment Summary */}
              <div className="bg-white rounded-lg p-4 border border-gray-200 space-y-2">
                <h4 className="font-medium text-gray-900 mb-3">Payment Summary</h4>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Price:</span>
                  <span className="font-medium text-gray-900" data-testid="preorder-total-price">
                    {formatCurrency(orderData.total_price)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-medium text-green-600" data-testid="preorder-amount-paid">
                    {formatCurrency(orderData.amount_paid)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-100">
                  <span className="font-medium text-gray-900">Balance Owing:</span>
                  <span className={`font-bold text-lg ${orderData.balance_owing > 0 ? 'text-amber-600' : 'text-green-600'}`} data-testid="preorder-balance">
                    {formatCurrency(orderData.balance_owing)}
                  </span>
                </div>
              </div>

              {/* Balance Owing Alert */}
              {orderData.balance_owing > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 text-sm">
                    <strong>Payment Due:</strong> You have a remaining balance of {formatCurrency(orderData.balance_owing)}. 
                    We'll contact you when your order is ready to ship to arrange final payment.
                  </p>
                </div>
              )}

              {/* Payment History */}
              {orderData.payment_history && orderData.payment_history.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowHistory(!showHistory)}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
                    data-testid="toggle-payment-history"
                  >
                    {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    {showHistory ? 'Hide' : 'Show'} Payment History ({orderData.payment_history.length})
                  </button>
                  
                  {showHistory && (
                    <div className="mt-2 space-y-2" data-testid="payment-history">
                      {orderData.payment_history.map((payment, idx) => (
                        <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm flex justify-between items-center">
                          <div>
                            <p className="font-medium text-gray-900 capitalize">{payment.type}</p>
                            <p className="text-gray-500">{formatDate(payment.date)}</p>
                            {payment.notes && <p className="text-gray-500 text-xs">{payment.notes}</p>}
                          </div>
                          <span className="font-medium text-green-600">+{formatCurrency(payment.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Check Another Order */}
              <Button
                variant="outline"
                onClick={() => {
                  setOrderData(null);
                  setOrderCode('');
                  setEmail('');
                  setError('');
                }}
                className="w-full"
                data-testid="check-another-order"
              >
                Check Another Order
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckPreOrderStatus;
